import { useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import RichTextEditor from "@/components/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Upload, Facebook, Twitter, Instagram, Linkedin, Youtube, Sparkles, Loader2, X, Tag, Plus, AtSign, MapPin, ImagePlus, Camera, Cloud, MessagesSquare, Mail, Hash, Video, Code } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { blogPostSchema, sanitizeText, sanitizeUrl, type BlogPostData } from "@/lib/validations";
import { useTags, type Tag as TagType } from "@/hooks/useTags";
import { PollEditor } from "@/components/polls/PollEditor";

interface BlogPost {
  id?: string;
  title: string;
  content: string;
  preview: string;
  image_url?: string;
  facebook_url?: string;
  twitter_url?: string;
  instagram_url?: string;
  linkedin_url?: string;
  youtube_url?: string;
  tiktok_url?: string;
  threads_url?: string;
  google_business_url?: string;
  pinterest_url?: string;
  snapchat_url?: string;
  bluesky_url?: string;
  reddit_url?: string;
  substack_url?: string;
  discord_url?: string;
  published: boolean;
}

interface BlogPostFormProps {
  post?: BlogPost;
  onSave: () => void;
  onCancel: () => void;
}

const BlogPostForm = ({ post, onSave, onCancel }: BlogPostFormProps) => {
  const { user } = useAuth();
  const { fetchPostTags, searchTags, createTag, updatePostTags } = useTags();
  const [formData, setFormData] = useState<BlogPost>({
    title: post?.title || "",
    content: post?.content || "",
    preview: post?.preview || "",
    image_url: post?.image_url || "",
    facebook_url: post?.facebook_url || "",
    twitter_url: post?.twitter_url || "",
    instagram_url: post?.instagram_url || "",
    linkedin_url: post?.linkedin_url || "",
    youtube_url: post?.youtube_url || "",
    tiktok_url: post?.tiktok_url || "",
    threads_url: post?.threads_url || "",
    google_business_url: post?.google_business_url || "",
    pinterest_url: post?.pinterest_url || "",
    snapchat_url: post?.snapchat_url || "",
    bluesky_url: post?.bluesky_url || "",
    reddit_url: post?.reddit_url || "",
    substack_url: post?.substack_url || "",
    discord_url: post?.discord_url || "",
    published: post?.published || false,
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedTags, setSelectedTags] = useState<TagType[]>([]);
  const [tagSearchQuery, setTagSearchQuery] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState<TagType[]>([]);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [editorMode, setEditorMode] = useState<'visual' | 'html'>('visual');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (post?.id) {
      fetchPostTags(post.id).then(setSelectedTags);
    }
  }, [post?.id]);

  useEffect(() => {
    const searchDebounce = setTimeout(async () => {
      if (tagSearchQuery.trim()) {
        const results = await searchTags(tagSearchQuery);
        const filtered = results.filter(tag => !selectedTags.find(t => t.id === tag.id));
        setTagSuggestions(filtered);
        setShowTagDropdown(true);
      } else {
        setTagSuggestions([]);
        setShowTagDropdown(false);
      }
    }, 300);

    return () => clearTimeout(searchDebounce);
  }, [tagSearchQuery, selectedTags]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    setUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}.${fileExt}`;

      // Load image to check dimensions
      const img = await createImageBitmap(file);
      
      // If image width > 350px, create resized version
      if (img.width > 350) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        
        // Calculate dimensions maintaining aspect ratio
        const targetWidth = 300;
        const scaleFactor = targetWidth / img.width;
        canvas.width = targetWidth;
        canvas.height = img.height * scaleFactor;
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Convert to blob
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((blob) => resolve(blob!), file.type);
        });
        
        // Upload original (for backup/full quality)
        const originalPath = `blog-images/${fileName}`;
        await supabase.storage
          .from('blog-images')
          .upload(originalPath, file);
        
        // Upload small version
        const smallFileName = fileName.replace(/\.(\w+)$/, '-small.$1');
        const smallPath = `blog-images/${smallFileName}`;
        const { error: uploadError } = await supabase.storage
          .from('blog-images')
          .upload(smallPath, blob);
          
        if (uploadError) throw uploadError;
        
        // Get URL for small version
        const { data } = supabase.storage
          .from('blog-images')
          .getPublicUrl(smallPath);
          
        setFormData((prev) => ({ ...prev, image_url: data.publicUrl }));
      } else {
        // Original is small enough, just upload as-is
        const filePath = `blog-images/${fileName}`;
        const { error: uploadError } = await supabase.storage
          .from('blog-images')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data } = supabase.storage
          .from('blog-images')
          .getPublicUrl(filePath);

        setFormData((prev) => ({ ...prev, image_url: data.publicUrl }));
      }
      
      toast.success("Image uploaded successfully!");
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEditorImageUpload = async (file: File): Promise<string> => {
    if (!user) {
      throw new Error("You must be logged in to upload images");
    }

    try {
      const fileExt = file.name.split(".").pop() || "jpg";
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

      // Load image to check dimensions
      const img = await createImageBitmap(file);
      
      // If image width > 350px, create resized version
      if (img.width > 350) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        
        // Calculate dimensions maintaining aspect ratio
        const targetWidth = 300;
        const scaleFactor = targetWidth / img.width;
        canvas.width = targetWidth;
        canvas.height = img.height * scaleFactor;
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Convert to blob
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((blob) => resolve(blob!), file.type);
        });
        
        // Upload original (for backup/full quality)
        const originalPath = `blog-images/${fileName}`;
        await supabase.storage
          .from('blog-images')
          .upload(originalPath, file);
        
        // Upload small version
        const smallFileName = fileName.replace(/\.(\w+)$/, '-small.$1');
        const smallPath = `blog-images/${smallFileName}`;
        const { error: uploadError } = await supabase.storage
          .from('blog-images')
          .upload(smallPath, blob);
          
        if (uploadError) throw uploadError;
        
        // Get URL for small version
        const { data } = supabase.storage
          .from('blog-images')
          .getPublicUrl(smallPath);
          
        return data.publicUrl;
      } else {
        // Original is small enough, just upload as-is
        const filePath = `blog-images/${fileName}`;
        const { error: uploadError } = await supabase.storage
          .from('blog-images')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data } = supabase.storage
          .from('blog-images')
          .getPublicUrl(filePath);

        return data.publicUrl;
      }
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const generateAIPreview = async () => {
    if (!formData.content || formData.content.trim() === "") {
      toast.error("Please add content before generating AI preview");
      return;
    }

    setGeneratingPreview(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-blog-preview", {
        body: {
          title: formData.title,
          content: formData.content,
        },
      });

      if (error) throw error;

      if (data?.preview) {
        setFormData((prev) => ({ ...prev, preview: data.preview }));
        toast.success("AI preview generated!");
      } else {
        throw new Error("No preview returned from AI");
      }
    } catch (error: any) {
      console.error("Error generating AI preview:", error);
      if (error.message?.includes("Rate limit")) {
        toast.error("AI rate limit reached, please try again later");
      } else if (error.message?.includes("credits")) {
        toast.error("AI credits depleted. Please add credits to continue.");
      } else {
        toast.error("Failed to generate preview");
      }
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handleSave = async () => {
    console.log('🔵 handleSave called');
    console.log('🔵 User:', user);
    console.log('🔵 Form data:', formData);
    
    if (!user) {
      console.error('❌ No user - blocking save');
      toast.error("You must be logged in to save posts");
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      // Auto-generate preview if empty and content exists
      let finalPreview = formData.preview;
      if (!finalPreview && (formData.title || formData.content)) {
        try {
          const { data } = await supabase.functions.invoke("generate-blog-preview", {
            body: {
              title: formData.title,
              content: formData.content,
            },
          });
          if (data?.preview) {
            finalPreview = data.preview;
          }
        } catch (error) {
          console.error("Auto-preview generation failed, using fallback:", error);
          // Fallback to old method if AI fails
          finalPreview = formData.content ? formData.content.slice(0, 200) + "..." : "";
        }
      }

      // Validate form data
      const validationData = {
        title: sanitizeText(formData.title),
        content: formData.content, // Keep HTML formatting from rich text editor
        preview: finalPreview ? sanitizeText(finalPreview) : undefined,
        facebook_url: sanitizeUrl(formData.facebook_url || ""),
        twitter_url: sanitizeUrl(formData.twitter_url || ""),
        instagram_url: sanitizeUrl(formData.instagram_url || ""),
        linkedin_url: sanitizeUrl(formData.linkedin_url || ""),
        youtube_url: sanitizeUrl(formData.youtube_url || ""),
        tiktok_url: sanitizeUrl(formData.tiktok_url || ""),
        threads_url: sanitizeUrl(formData.threads_url || ""),
        google_business_url: sanitizeUrl(formData.google_business_url || ""),
        pinterest_url: sanitizeUrl(formData.pinterest_url || ""),
        snapchat_url: sanitizeUrl(formData.snapchat_url || ""),
        bluesky_url: sanitizeUrl(formData.bluesky_url || ""),
        reddit_url: sanitizeUrl(formData.reddit_url || ""),
        substack_url: sanitizeUrl(formData.substack_url || ""),
        discord_url: sanitizeUrl(formData.discord_url || ""),
      };

      console.log('Validation data:', validationData);
      const validation = blogPostSchema.safeParse(validationData);

      if (!validation.success) {
        console.error('Validation failed:', validation.error);
        const fieldErrors: Record<string, string> = {};
        validation.error.issues.forEach((error) => {
          if (error.path[0]) {
            fieldErrors[error.path[0] as string] = error.message;
          }
        });
        setErrors(fieldErrors);
        toast.error("Please fix the validation errors");
        return;
      }
      
      console.log('Validation passed, validated data:', validation.data);

      const postData = {
        ...validation.data,
        user_id: user.id,
        image_url: formData.image_url,
        published: formData.published,
        preview: validation.data.preview || "",
      };

      let postId = post?.id;

      console.log('Saving post data:', postData);
      if (post?.id) {
        console.log('Updating existing post:', post.id);
        const { error } = await supabase.from("blog_posts").update(postData).eq("id", post.id);
        if (error) {
          console.error('Update error:', error);
          throw error;
        }
        console.log('Post updated successfully');
      } else {
        console.log('Creating new post');
        const { data: newPost, error } = await supabase.from("blog_posts").insert([postData]).select().single();
        if (error) {
          console.error('Insert error:', error);
          throw error;
        }
        postId = newPost.id;
        console.log('New post created:', postId);
      }

      // Update tags
      if (postId) {
        const tagIds = selectedTags.map(t => t.id);
        await updatePostTags(postId, tagIds);
      }

      toast.success(post?.id ? "Post updated successfully!" : "Post created successfully!");
      onSave();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const socialIcons = {
    facebook: Facebook,
    twitter: Twitter,
    instagram: Instagram,
    linkedin: Linkedin,
    youtube: Youtube,
    tiktok: Video,
    threads: AtSign,
    google_business: MapPin,
    pinterest: ImagePlus,
    snapchat: Camera,
    bluesky: Cloud,
    reddit: MessagesSquare,
    substack: Mail,
    discord: Hash,
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>{post ? "Edit Post" : "Create New Post"}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="Enter post title"
          />
          {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="preview">Preview</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={generateAIPreview}
              disabled={generatingPreview || !formData.content || formData.content.trim() === ""}
              className="gap-2"
            >
              {generatingPreview ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-3 h-3" />
                  Generate AI Preview
                </>
              )}
            </Button>
          </div>
          <Textarea
            id="preview"
            value={formData.preview}
            onChange={(e) => setFormData((prev) => ({ ...prev, preview: e.target.value }))}
            placeholder="Short preview (AI will generate if empty, or click 'Generate AI Preview')"
            rows={3}
            maxLength={300}
          />
          <p className="text-xs text-muted-foreground">{formData.preview.length}/300 characters</p>
          {errors.preview && <p className="text-sm text-destructive">{errors.preview}</p>}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="content">Content</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditorMode(editorMode === 'visual' ? 'html' : 'visual')}
              className="gap-2"
            >
              <Code className="w-3 h-3" />
              {editorMode === 'visual' ? 'Edit HTML' : 'Visual Editor'}
            </Button>
          </div>
          {editorMode === 'visual' ? (
            <RichTextEditor
              content={formData.content}
              onChange={(content) => setFormData((prev) => ({ ...prev, content }))}
              onImageUpload={handleEditorImageUpload}
              placeholder="Start writing your blog post content..."
            />
          ) : (
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
              placeholder="Enter HTML content..."
              rows={20}
              className="font-mono text-sm"
            />
          )}
          {errors.content && <p className="text-sm text-destructive">{errors.content}</p>}
        </div>

        <div className="space-y-4">
          <Label>Featured Image</Label>
          <div className="flex items-center gap-4">
            <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Uploading..." : "Upload Image"}
            </Button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
          </div>
          {formData.image_url && (
            <div className="mt-2">
              <img src={formData.image_url} alt="Preview" className="w-32 h-32 object-cover rounded-md" />
            </div>
          )}
        </div>

        <div className="space-y-4">
          <Label>Social Media Links</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(socialIcons).map(([platform, Icon]) => (
              <div key={platform} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  <Input
                    placeholder={`${platform.charAt(0).toUpperCase() + platform.slice(1)} URL`}
                    value={(formData[`${platform}_url` as keyof BlogPost] as string) || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [`${platform}_url`]: e.target.value,
                      }))
                    }
                  />
                </div>
                {errors[`${platform}_url`] && <p className="text-sm text-destructive">{errors[`${platform}_url`]}</p>}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <Label>Tags</Label>
          <div className="relative">
            <div className="flex flex-wrap gap-2 p-3 border border-input rounded-md bg-background min-h-[60px]">
              {selectedTags.map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center gap-1 bg-primary/10 text-primary border border-primary/20 rounded-full px-3 py-1 text-sm"
                >
                  {tag.name}
                  <button
                    type="button"
                    onClick={() => setSelectedTags(selectedTags.filter(t => t.id !== tag.id))}
                    className="hover:bg-primary/20 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <div className="flex-1 min-w-[200px]">
                <Input
                  value={tagSearchQuery}
                  onChange={(e) => setTagSearchQuery(e.target.value)}
                  onFocus={() => tagSearchQuery && setShowTagDropdown(true)}
                  onBlur={() => setTimeout(() => setShowTagDropdown(false), 200)}
                  placeholder={selectedTags.length === 0 ? "Start typing to add tags..." : "Add more tags..."}
                  className="border-0 focus-visible:ring-0 p-0 h-8"
                />
              </div>
            </div>
            {showTagDropdown && tagSuggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {tagSuggestions.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => {
                      if (selectedTags.length < 10) {
                        setSelectedTags([...selectedTags, tag]);
                        setTagSearchQuery("");
                        setShowTagDropdown(false);
                      } else {
                        toast.error("Maximum 10 tags per post");
                      }
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-accent text-sm flex items-center gap-2"
                  >
                    <Tag className="w-3 h-3" />
                    {tag.name}
                  </button>
                ))}
              </div>
            )}
            {showTagDropdown && tagSearchQuery && tagSuggestions.length === 0 && (
              <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg">
                <button
                  type="button"
                  onClick={async () => {
                    if (selectedTags.length >= 10) {
                      toast.error("Maximum 10 tags per post");
                      return;
                    }
                    const newTag = await createTag(tagSearchQuery);
                    if (newTag) {
                      setSelectedTags([...selectedTags, newTag]);
                      setTagSearchQuery("");
                      setShowTagDropdown(false);
                    }
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-accent text-sm flex items-center gap-2"
                >
                  <Plus className="w-3 h-3" />
                  Create new tag: "{tagSearchQuery}"
                </button>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{selectedTags.length}/10 tags selected</p>
        </div>

        <PollEditor postId={post?.id || null} published={formData.published} />

        <div className="flex items-center space-x-2">
          <Switch
            id="published"
            checked={formData.published}
            onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, published: checked }))}
          />
          <Label htmlFor="published">Publish post</Label>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Post"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BlogPostForm;
